import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { initSmoothScroll, scrollToSection } from "@/lib/scroll";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Manifesto } from "@/components/Manifesto";
import { Services } from "@/components/Services";
import { HowItWorks } from "@/components/HowItWorks";
import { ForFamilies } from "@/components/ForFamilies";
import { BecomeCompanion } from "@/components/BecomeCompanion";
import { Testimonials } from "@/components/Testimonials";
import { FAQSection } from "@/components/FAQSection";
import { ContactSection } from "@/components/ContactSection";
import { ConsultationModal } from "@/components/ConsultationModal";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, CalendarHeart } from "lucide-react";

const CtaBand = ({ onBook }) => (
  <section data-testid="cta-band" className="relative overflow-hidden py-24 lg:py-32">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-1/2 h-[30rem] w-[30rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-mauve/15 blur-3xl"
    />
    <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
      <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-mauve">
        Ready When You Are
      </p>
      <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
        Someone warm is only a <span className="italic text-mauve-dark">conversation</span> away.
      </h2>
      <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-charcoal/75">
        A free, friendly consultation is the gentlest first step. Tell us about your
        loved one and we'll take it from there.
      </p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
        <button
          type="button"
          onClick={onBook}
          data-testid="cta-band-primary"
          className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-cream shadow-elevated transition-all duration-300 hover:-translate-y-0.5 hover:bg-mauve-dark"
        >
          <CalendarHeart className="h-4 w-4" aria-hidden="true" />
          Book a Consultation
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
        </button>
        <a
          href="#contact"
          onClick={(e) => scrollToSection(e, "#contact")}
          data-testid="cta-band-secondary"
          className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/70 px-8 py-4 text-sm font-semibold text-navy backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-mauve/40 hover:bg-mauve-light"
        >
          Send an Enquiry
        </a>
      </div>
    </Reveal>
  </section>
);

export default function App() {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => initSmoothScroll(), []);

  const openModal = () => setModalOpen(true);

  return (
    <div className="min-h-screen overflow-x-clip bg-cream text-charcoal" data-testid="adeya-app">
      <Navbar onBook={openModal} />
      <main>
        <Hero onBook={openModal} />
        <Marquee />
        <Manifesto />
        <Services onBook={openModal} />
        <HowItWorks />
        <ForFamilies />
        <BecomeCompanion onBook={openModal} />
        <Testimonials />
        <FAQSection />
        <ContactSection />
        <CtaBand onBook={openModal} />
      </main>
      <Footer />
      <ConsultationModal open={modalOpen} onOpenChange={setModalOpen} />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
